import { createEffect, createSignal, on, onCleanup, Show } from "solid-js";
import { Spinner } from "./Spinner";

const KNOB = 42;
/**
 * The knob's icon must never take the touch: on iOS Safari a press on an <img> selects or drags the
 * image instead of moving the knob.
 */
const KNOB_ICON = {
  width: 26,
  height: 26,
  draggable: false,
  class: "pointer-events-none select-none [-webkit-user-drag:none] [-webkit-touch-callout:none]",
} as const;
const INSET = 4;

/**
 * Figma tam/slide-to-confirm (end ride, booking, cancel, pause): 50 px, 4 px radius, square 42 px knob.
 * Default: chevrons knob on the left, label between 54 px and 16 px. Done: knob on the right with a
 * check, label between 16 px and 56 px. A long-running action swaps the check for a spinner after
 * a moment. Keyboard: Enter/Space confirms.
 */
export function SlideToConfirm(props: {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  /** The confirmed action is running: the knob stays at the end. */
  loading?: boolean;
}) {
  const inactive = () => !!props.disabled || !!props.loading;
  let track!: HTMLButtonElement;
  const [x, setX] = createSignal(0);
  const [dragging, setDragging] = createSignal(false);
  const [done, setDone] = createSignal(false);
  const [slow, setSlow] = createSignal(false);
  const max = () => Math.max(0, track.clientWidth - KNOB - 2 * INSET);
  // Back to the start whenever it becomes usable again (warning dismissed, refusal, error).
  createEffect(
    on(inactive, (now, was) => {
      if (was && !now) {
        setX(0);
        setDone(false);
      }
    }),
  );
  // The check first (Figma done state), then a spinner if the action takes a while.
  createEffect(() => {
    if (!props.loading) return setSlow(false);
    const t = setTimeout(() => setSlow(true), 600);
    onCleanup(() => clearTimeout(t));
  });
  const confirm = () => {
    setX(max());
    setDone(true);
    props.onConfirm();
  };

  const onPointerDown = (e: PointerEvent) => {
    if (inactive()) return;
    e.preventDefault(); // no text/image selection or native drag from the press
    setDragging(true);
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // capture refused: the drag still works while the finger stays on the knob
    }
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!dragging()) return;
    const rect = track.getBoundingClientRect();
    setX(Math.min(max(), Math.max(0, e.clientX - rect.left - KNOB / 2 - INSET)));
  };
  const onPointerUp = () => {
    if (!dragging()) return;
    setDragging(false);
    if (x() >= max() * 0.9) confirm();
    else setX(0);
  };
  const atEnd = () => done() || !!props.loading;

  return (
    <button
      type="button"
      ref={track}
      data-sheet-nodrag
      class={`block w-full relative h-[50px] rounded-[4px] bg-green text-white select-none overflow-hidden shadow-[0_1.5px_1.5px_rgba(0,0,0,0.18)] ${props.disabled && !props.loading ? "opacity-50" : ""}`}
      aria-label={props.label}
      aria-busy={props.loading || undefined}
      disabled={inactive()}
      onClick={(e) => {
        // Keyboard/assistive activation confirms; pointer users must drag the knob.
        if (e.detail === 0) confirm();
      }}
    >
      <span
        class="absolute top-1/2 -translate-y-1/2 text-center font-medium uppercase text-base leading-normal tracking-[0.32px]"
        style={atEnd() ? { left: "16px", right: "56px" } : { left: "54px", right: "16px" }}
      >
        {props.label}
      </span>
      <span
        class="absolute rounded-[3px] bg-white text-green flex items-center justify-center touch-none select-none [-webkit-touch-callout:none]"
        style={{
          top: `${INSET}px`,
          left: `${INSET}px`,
          width: `${KNOB}px`,
          height: `${KNOB}px`,
          transform: `translateX(${atEnd() && !dragging() ? max() : x()}px)`,
          transition: dragging() ? "none" : "transform 150ms",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <Show
          when={atEnd()}
          fallback={<img src="/icons/knob-chevrons.svg" {...KNOB_ICON} alt="" />}
        >
          <Show when={slow()} fallback={<img src="/icons/knob-check.svg" {...KNOB_ICON} alt="" />}>
            <Spinner size={22} class="pointer-events-none" />
          </Show>
        </Show>
      </span>
    </button>
  );
}
